import Point from '@arcgis/core/geometry/Point';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Dropdown, Popover, OverlayTrigger } from 'react-bootstrap';
import { EpiSettings } from '../../dto/epi-settings';
import { MAP_SET_PROXIMITY } from '../../services/map-controller';
import { addFilterCounts, interleavePrek, lcFirst } from '../../utility/api-utilities';
import HideLogBoundary from '../error/hide-log-boundary';
import FilterForm from '../inputs/filter-form';
import CompareSchools from '../pages/compare-schools';
import { AppContext } from '../school-search-app';
import { proximities } from './map-container-config';
import ResultCard from './result-card';
import ResultListEntry from './result-list-entry';
import { PAGE_MODE } from '../../interfaces'
import { getPageProperties } from '../../services/cps-school-api';
export interface IResultListProps {
    max: number,
    selectSchool: (schoolId: number) => void,
    radiusSearch: (proximity: number, point) => void,
    resultsInfoString?: string,
    viewMode: string,
    openCompareSchools: (schools) => void;
    compareSchoolsList?: any[];
    onDeleteCompareSchoolsList?: (schools) => void;
    schoolListError?: string;
    showLoading: boolean;
    tier: number;
}

function ResultList({
    openCompareSchools,
    viewMode,
    selectSchool,
    resultsInfoString,
    compareSchoolsList,
    onDeleteCompareSchoolsList,
    schoolListError,
    showLoading
}: IResultListProps) {

    const INITIAL_RESULT_COUNT = 20;

    const [compareSchools, setCompareSchools] = useState([]);
    const [max, setMax] = useState(INITIAL_RESULT_COUNT)
    const [onViewOnMapClick, setOnViewOnMapClick] = useState(false)
    //const [focus, setFocus] = useState(null) // hoisting into context to let search bar work. clean up after other map stuff is merged
    //const [selectedSchoolId, setSelectedSchoolId] = useState<any>();
    const appContext = useContext(AppContext);
    let results = [...appContext.resultSchools];
    const compareIds = compareSchools.map(s => s._epi.id);
    const [profileRedirectUrl, setProfileRedirectUrl] = useState(null);

    const isRadius = appContext.mapController.pin != null;

    const selectedSchoolId = appContext.mapSelectedSchoolId; // this is actually the whole school. change the name next sprint

    const [comparePopoverShow, setComparePopoverShow] = useState(false)

    if (results.length && appContext.mode == PAGE_MODE.PREK) {
        results = interleavePrek(results);
    }

    let displaySchools = appContext.searchProcessor.mutator(appContext.searchProcessor.sort(results)).slice(0, max);

    const addCompareSchool = (school, add) => {
        const schools = add ? [...compareSchools, school] : compareSchools.filter(s => school._epi.id != s._epi.id)
        setCompareSchools(schools);
        onDeleteCompareSchoolsList(schools);
    }

    useEffect(() => {
        getPageProperties().then((properties) => {
            setProfileRedirectUrl(properties.schoolProfilePageUrl);
        })
    }, [])

    useEffect(() => {
        if (compareSchoolsList) {
            setCompareSchools(compareSchoolsList);
        }
    }, [compareSchoolsList]);

    const compare = () => {
        openCompareSchools(compareSchools);
    }

    const focusSchool = (id) => {
        setOnViewOnMapClick(true);
        appContext.setFocusedSchool(id)
    }

    const cards = displaySchools => <div className={`card-grid ${appContext.mode}`}>
        {displaySchools.map((school, index) => {
            const query = new URLSearchParams((window.location.search).toLowerCase())
            const schoolSearchId = query.get("schoolsearchid")

            if (schoolSearchId) {
                if (String(schoolSearchId) === String(school._epi.id)) {
                    return (
                        <HideLogBoundary key={`card bound ${index}`}>
                            <ResultCard school={school}
                                schoolInfo={selectSchool} compareSchool={addCompareSchool} compareSchools={compareSchools}
                                focusMap={focusSchool} setOnViewOnMapClick={setOnViewOnMapClick} profileRedirectUrl={profileRedirectUrl}
                            />
                        </HideLogBoundary>
                    )
                } else {
                    return <></>
                }
            }
            return (
                <HideLogBoundary key={`card bound ${index}`}>
                    <ResultCard school={school}
                        schoolInfo={selectSchool} compareSchool={addCompareSchool} compareSchools={compareSchools}
                        focusMap={focusSchool} setOnViewOnMapClick={setOnViewOnMapClick} profileRedirectUrl={profileRedirectUrl}
                    />
                </HideLogBoundary>
            )
        })}
    </div>;

    const list = displaySchools => {
        let headers = <></>;
        if (displaySchools.length) {
            if (appContext.mode == PAGE_MODE.GOCPS) {
                headers = <thead>
                    <tr>
                        <th>Compare</th>
                        <th>School Name</th>
                        <th>Grades Served</th>
                        <th>Number of Students</th>
                        {/*<th>Seats</th>*/}
                    </tr>
                </thead>;
            }
            else { // mode is prek
                headers = <thead>
                    <tr>
                        <th>Compare</th>
                        <th>School Name</th>
                        <th>School Type</th>
                        <th>Duration</th>
                        <th>School Day</th>
                        <th>Seats</th>
                    </tr>
                </thead>;
            }
        }
        return <table className="results-table">
            {headers}
            <tbody>
                {displaySchools.map((school, index) => {
                    return <ResultListEntry profileRedirectUrl={profileRedirectUrl} school={school} key={`${school.title} ${index}`} schoolInfo={selectSchool} focusMap={focusSchool}
                        compareSchool={addCompareSchool} isCompareSchool={compareIds.includes(school._epi.id)} />
                })}
            </tbody>
        </table>;
    }

    function setIndex(index: number) {
        const width = window.innerWidth;
        if (width > 992) { return index + 2; }
        if (width > 768) { return index + 1; }
        return index;
    }

    function onMarkerClick() {
        const parentEl = document.getElementsByClassName('results-list');
        const isOdd = (num) => num % 2;
        if (selectedSchoolId) {
            let index: number = results.findIndex(i => i._epi.id === selectedSchoolId._epi.id) + 3;
            isOdd(index) && (index += 1)
            setMax(Math.max(max, setIndex(index)));
            if (!onViewOnMapClick) {
                setTimeout(() => {
                    try {
                        const card = parentEl[0].querySelector(`[data-schoolId="${selectedSchoolId._epi.id}"]`)
                        const points = () => {
                            const thead = parentEl[0].querySelector('thead');

                            if (appContext.resultAlert) {
                                const resAllertHeight = document.getElementsByClassName('result-alert')
                                // @ts-ignore
                                return viewMode === 'list' ? thead.offsetHeight : resAllertHeight[0].offsetHeight + 50;
                            } else {
                                const pixels = viewMode === 'list' ? thead.offsetHeight : 50;
                                return pixels;
                            }
                        }
                        // @ts-ignore
                        parentEl[0].scrollTop = card.offsetTop - points();
                    } catch (e) {
                        console.log('error scrolling', e)
                    }
                }, 0);
            }
        } else {
            setMax(INITIAL_RESULT_COUNT);
            setTimeout(() => {
                try {
                    parentEl && parentEl[0].scrollTo(0, 0)
                } catch (e) {
                    console.log('error scrolling', e)
                }
            }, 0);
        }
        setOnViewOnMapClick(false);

    }
    useEffect(() => {
        onMarkerClick()
    }, [selectedSchoolId, viewMode, appContext.resultAlert])

    useEffect(() => {
        function hendleResize() {
            // onMarkerClick();
        }
        window.addEventListener("resize", hendleResize);
        return () => window.removeEventListener("resize", hendleResize);

    });

    useEffect(() => {
        if (compareSchools.length == 0) {
            setComparePopoverShow(false)
        };
    }, [compareSchools]);

    function deleteCompareSchoole(schoolId) {
        if (compareSchools.some(s => s._epi.id === schoolId)) {
            const schools = compareSchools.filter(s => s._epi.id !== schoolId);
            setCompareSchools([...schools]);
            onDeleteCompareSchoolsList([...schools]);
        }
    }

    const comparePopover = (
        <Popover id="popover-compare">
            <Popover.Title>
                <div className="popoverFooter">
                    <span onClick={() => setCompareSchools([])} className="compareResetBtn">Reset</span>
                    <i className="compare_CloseIcon big" onClick={() => { setComparePopoverShow(false); }}></i>
                </div>
            </Popover.Title>
            <Popover.Content>
                {
                    compareSchools.map((school) => {
                        return <div key={'compareItem_' + school._epi.id} className="compareItem" >
                            {school._epi.title}
                            <i className="compare_CloseIcon" onClick={() => { deleteCompareSchoole(school._epi.id); }}></i>
                        </div>
                    })
                }
            </Popover.Content>
        </Popover>
    );

    const renderResults = viewMode == 'list' ? list : cards;

    function setProximity(radius) {
        appContext.mapControllerDispatch({
            action: MAP_SET_PROXIMITY,
            proximity: radius
        });
    }

    return (
        <div className={`results-overlay`}>
            <div className={`results-area ${viewMode}`}>
                <div className="results-header">
                    {/*<PrekInputForm reset={resetCount} isShow={sidebarFilters} />*/}
                    <FilterForm reset={0} isShow={null} />
                    <div className="results-count bs-shim">
                        <div>Showing {!showLoading ? displaySchools.length : 0} of {!showLoading ? results.length : 0}
                            {isRadius && <> within
                                <Dropdown className="simple-dropdown">
                                    <Dropdown.Toggle>
                                        {appContext.mapController.proximity} mi
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {proximities.map(opt => <Dropdown.Item value={opt.value} key={`proximity${opt.value}`}
                                            onClick={() => setProximity(opt.value)}>{opt.label}</Dropdown.Item>)}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </>}</div>
                        <div style={{ display: compareSchools.length ? 'flex' : 'none' }} className="compare-block">
                            <button className="compare-button" style={{ display: compareSchools.length ? 'initial' : 'none' }}
                                onClick={compare}>Compare</button>
                            <OverlayTrigger trigger="click" placement="bottom" overlay={comparePopover} show={comparePopoverShow}>
                                <div className="arrowContainer" onClick={() => setComparePopoverShow(!comparePopoverShow)}>
                                    <span className="arrowDown"></span>
                                </div>
                            </OverlayTrigger>
                        </div>
                        <button onClick={appContext.resetSearch} className="card-reset-btn">Reset</button>
                    </div>
                </div>

                {appContext.resultAlert && <div className="result-alert">
                    {appContext.resultAlert}
                </div>}

                {(schoolListError && schoolListError.length) && <div className="errorMessage">
                    {schoolListError}
                </div>}

                {showLoading && <div className="loadingGif_container loading_profile"><img src="https://www.cps.edu/globalassets/cps-global-media/icons/spinner.gif" alt="spinner" /></div>}

                {(!schoolListError || schoolListError.length === 0) &&
                    <div className={`results-list`}>
                        {renderResults(displaySchools)}
                    </div>}

                {((!schoolListError || schoolListError.length === 0) && !showLoading) &&
                    <div className="show-more">
                        {max < results.length ?
                            <button className="btn" onClick={() => setMax(max + 10)}>Show More</button> : <></>}
                    </div>}
            </div>
        </div>
    )
}

export default ResultList;
